import {useMemo} from 'react'
import {useIntl} from 'react-intl'
import {NEW_PRODUCT_BADGE_DURATION, DEAL_PROMO_KEY} from '../constants'
import useMultiSite from './use-multi-site'
import {PROMOTION_BADGE_MESSAGES} from '../constants'
import {usePromotions} from '@salesforce/commerce-sdk-react/hooks/ShopperPromotions/query'
import {normalizeColorWorld} from '../utils/utils'
import {PROMOTION_BADGE_COLOR_WORLDS} from '../components/badge-group/badge.recipe'

/**
 * Resolves the badge color of a promotion badge from its Color World.
 * Deal badges keep their own color; empty or unsupported values fall back to the default.
 * @param {string} badgeKey - the promotion badge key
 * @param {string} rawColorWorld - the promotion Color World as sent by the API
 * @returns {string|undefined} a supported badge Color World, or undefined
 */
const getBadgeColorWorld = (badgeKey, rawColorWorld) => {
    if (badgeKey === DEAL_PROMO_KEY) return undefined
    const colorWorld = normalizeColorWorld(rawColorWorld)
    return PROMOTION_BADGE_COLOR_WORLDS.includes(colorWorld) ? colorWorld : undefined
}

export const useBadges = (product, promotions) => {
    const {formatMessage} = useIntl()
    const {site} = useMultiSite()

    // precompute the badge promotion ids to avoid re-rendering
    const badgePromotionsIds = useMemo(
        () => product?.productPromotions?.map(({promotionId}) => promotionId) || [],
        [product]
    )

    // get the promotions which are missing in the preloaded list
    const missingPromotionIds = useMemo(
        () =>
            badgePromotionsIds?.filter(
                (promotionId) => promotionId && !promotions?.find(({id}) => id === promotionId)
            ),
        [promotions, badgePromotionsIds]
    )

    // Fetch additional promotions for badges
    // stringify the params to avoid re-executing the effect when the same ids are passed
    const missingPromotionsIdsParam = useMemo(
        () => missingPromotionIds?.sort((a, b) => String(a).localeCompare(b)).join(','),
        [missingPromotionIds]
    )

    const {data} = usePromotions(
        {parameters: {ids: missingPromotionsIdsParam}},
        {enabled: !!missingPromotionsIdsParam}
    )
    // the list of badge promotions to display - combining preloaded and fetched
    const badgePromotions = useMemo(
        () =>
            badgePromotionsIds
                ?.map((promotionId) =>
                    [...(promotions || []), ...(data?.data || [])].find((p) => p.id === promotionId)
                )
                .filter(Boolean),
        [promotions, data, badgePromotionsIds]
    )

    return useMemo(() => {
        /* Current & Online From timestamps in milliseconds */
        const currentMs = Date.now()
        const onlineFromMs = new Date(
            product?.validFrom?.[`default@${site.id}`] || product?.validFrom?.default
        ).getTime()

        /* Returns true if product is added less than 6 months ago */
        const isNew = onlineFromMs && currentMs - onlineFromMs < NEW_PRODUCT_BADGE_DURATION

        const newBadge = isNew && {
            text: formatMessage({
                id: 'product_detail.badge.new',
                defaultMessage: 'New'
            }),
            variant: 'new'
        }

        const promotionalBadge = product?.c_PromotionalTags && {
            text: product?.c_PromotionalTags,
            variant: 'productPromo'
        }

        const promotionBadges =
            (badgePromotions || [])
                ?.filter(({c_badge}) => PROMOTION_BADGE_MESSAGES[c_badge])
                .map(({c_badge, c_colorWorld}) => ({
                    text: formatMessage(PROMOTION_BADGE_MESSAGES[c_badge]),
                    variant: c_badge === DEAL_PROMO_KEY ? 'deal' : 'promotions',
                    colorWorld: getBadgeColorWorld(c_badge, c_colorWorld)
                })) || []

        const plpSearchHitPromotionBadges =
            product?.c_badges?.map?.((badge) => ({
                text: formatMessage(PROMOTION_BADGE_MESSAGES[badge]),
                variant: badge === DEAL_PROMO_KEY ? 'deal' : 'promotions',
                colorWorld: getBadgeColorWorld(badge, product?.c_badgeColorWorlds?.[badge])
            })) || []

        return [
            newBadge,
            promotionalBadge,
            ...promotionBadges,
            ...plpSearchHitPromotionBadges
        ].filter(Boolean)
    }, [badgePromotions, formatMessage, product, site])
}
